/**
 * Wires phoneCaptions.ts to the room, the phone leg and the queue socket, on
 * web and native. Only an interpreter's room acts; in anyone else's room it
 * only listens and does nothing.
 *
 * - Who has captions on: Jitsi's requestingTranscription property of the
 *   other people in the room, re-read when it changes or someone leaves.
 * - The phone leg: twilioVoiceService statusChange (connected or not).
 * - The call: the stored active call (vrs_active_call) when it names this room.
 * - Each phone_caption from the server is shown to everyone in the room as a
 *   caption line (phoneCaptionMessage), and to this interpreter as well.
 */
import { CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE } from '../base/conference/actionTypes';
import { endpointMessageReceived } from '../base/conference/actions';
import { JitsiConferenceEvents } from '../base/lib-jitsi-meet';
import { getLocalParticipant } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { isInterpreter } from '../base/user-role/functions';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { QUEUE_CAPABILITIES_UPDATED } from '../interpreter-queue/actionTypes';
import { getPersistentJson } from '../vrs-auth/storage';
import { createPhoneCaptionsController, phoneCaptionMessage, someoneWantsCaptions } from './phoneCaptions';
import { twilioVoiceService } from './services/TwilioVoiceService';
MiddlewareRegistry.register(store => {
    const controller = createPhoneCaptionsController({
        clearTimeout: handle => clearTimeout(handle),
        now: () => Date.now(),
        send: (callId, on) => queueService.setPhoneCaptions(callId, on),
        setTimeout: (callback, ms) => setTimeout(callback, ms)
    });
    let listening = false;
    let conferenceListening;
    const snapshot = () => {
        const state = store.getState();
        const { conference, room } = state['features/base/conference'];
        const call = getPersistentJson('vrs_active_call');
        const callId = typeof call?.callId === 'string' && typeof call.roomName === 'string' && room
            && call.roomName.toLowerCase() === String(room).toLowerCase() ? call.callId : undefined;
        return {
            callId,
            enabled: state['features/interpreter-queue']?.capabilities?.phoneCaptions === true,
            interpreter: isInterpreter(),
            phoneLive: twilioVoiceService.getCallStatus?.()?.status === 'connected',
            wanted: conference ? someoneWantsCaptions(conference.getParticipants?.() || []) : false
        };
    };
    const refresh = () => controller.update(snapshot());
    const showCaption = (caption) => {
        const state = store.getState();
        const { conference } = state['features/base/conference'];
        if (!conference || caption.callId !== controller.requestedFor()) {
            return;
        }
        const localId = getLocalParticipant(state)?.id || 'interpreter';
        const message = phoneCaptionMessage(caption, localId, Date.now());
        try {
            conference.sendEndpointMessage('', message);
        }
        catch {
            // The room is going away; the next caption tries again.
        }
        store.dispatch(endpointMessageReceived({ id: localId,
            name: message.participant.name }, message));
    };
    // The queue listener generation of the queue listeners below. A sign-out
    // removes every queue listener (queueService.disconnect) while the app
    // stays open; the next interpreter room registers them again.
    let queueListenersFor;
    const listenOnce = () => {
        if (queueListenersFor !== queueService.listenerGeneration()) {
            queueListenersFor = queueService.listenerGeneration();
            queueService.on('phoneCaption', showCaption);
            queueService.on('phoneCaptionsState', state => controller.serverState(state));
            queueService.on('authenticated', () => controller.reconnected());
        }
        if (listening) {
            return;
        }
        listening = true;
        twilioVoiceService.on?.('statusChange', refresh);
    };
    const listenToConference = (conference) => {
        if (!conference || conferenceListening === conference) {
            return;
        }
        conferenceListening = conference;
        conference.on(JitsiConferenceEvents.PARTICIPANT_PROPERTY_CHANGED, (_participant, name) => {
            if (name === 'requestingTranscription') {
                refresh();
            }
        });
        conference.on(JitsiConferenceEvents.USER_LEFT, refresh);
        conference.on(JitsiConferenceEvents.USER_JOINED, refresh);
    };
    return next => action => {
        const result = next(action);
        switch (action.type) {
            case CONFERENCE_JOINED:
                if (isInterpreter()) {
                    listenOnce();
                    listenToConference(action.conference);
                    refresh();
                }
                break;
            case QUEUE_CAPABILITIES_UPDATED:
                if (listening) {
                    refresh();
                }
                break;
            case CONFERENCE_WILL_LEAVE:
            case CONFERENCE_LEFT:
                controller.stop();
                conferenceListening = undefined;
                break;
        }
        return result;
    };
});
